import React, { useCallback, useEffect, useReducer, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import Loading from "../Common/Loading";

import popimg   from "../Common/Images/ImagesForAlert/popup_bg1.png";
import popbg    from "../Common/Images/ImagesForAlert/popup_bg2.png";
import popclose from "../Common/Images/ImagesForAlert/popup_close_n.png";
import bg       from "../SYSADMIN/MathsDoctor/Images/top_blue_panel.png";
import AnsCorrect  from "../SYSADMIN/MathsDoctor/Images/AnsCorrect.GIF";
import AnsShouldbe from "../SYSADMIN/MathsDoctor/Images/AnsShouldbe.GIF";
import AnsWrong    from "../SYSADMIN/MathsDoctor/Images/AnsWrong.GIF";

import large_objective_n            from "../SYSADMIN/MathsDoctor/Images/Tablet/large_objective_n.png";
import large_objective_d            from "../SYSADMIN/MathsDoctor/Images/Tablet/large_objective_d.png";
import medium_objective_n           from "../SYSADMIN/MathsDoctor/Images/Tablet/medium_objective_n.png";
import medium_objective_d           from "../SYSADMIN/MathsDoctor/Images/Tablet/medium_objective_d.png";
import small_objective_n            from "../SYSADMIN/MathsDoctor/Images/Tablet/small_objective_n.png";
import small_objective_d            from "../SYSADMIN/MathsDoctor/Images/Tablet/small_objective_d.png";
import large_objective_shortened_n  from "../SYSADMIN/MathsDoctor/Images/Tablet/large_objective_shortened_n.png";
import large_objective_shortened_d  from "../SYSADMIN/MathsDoctor/Images/Tablet/large_objective_shortened_d.png";
import medium_objective_shortened_n from "../SYSADMIN/MathsDoctor/Images/Tablet/medium_objective_shortened_n.png";
import medium_objective_shortened_d from "../SYSADMIN/MathsDoctor/Images/Tablet/medium_objective_shortened_d.png";
import small_objective_shortened_n  from "../SYSADMIN/MathsDoctor/Images/Tablet/small_objective_shortened_n.png";
import small_objective_shortened_d  from "../SYSADMIN/MathsDoctor/Images/Tablet/small_objective_shortened_d.png";

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

// ─── Interfaces ───────────────────────────────────────────────────────────────

interface QnTableRow {
  Chronology: number;
  Strand: string;
  SubStrand: string;
  Yr: string;
  Topic: string;
  QnNo: string;
  Result: string;
  TimeTaken: number;
  CorrectAns: string;
  WrongAns: string;
  OptOrder: string;
  BlankAns: string;
}

interface HeaderInfo {
  SchoolCode: string;
  FamilyCode: string;
  Login: string;
  Year: string;
  Score: string;
  TestDate: string;
  TimeTaken: string;
  Country?: string;
  MDCurriculum?: string;
}

interface QuestionData {
  qn_file_url: string;
  qn_html?: string;
  qn_catg: string;
  lesson_code: string;
  fname: string;
  strand: number;
  substrand: number;
  year: number;
  topic: number;
  qn_no: number;
  country: string;
  curriculum: string;
  strand_label: string;
  substrand_label: string;
  topic_label: string;
  virtual_keypad: string;
  show_filter: boolean;
  show_nav: boolean;
  show_wrong_ans: boolean;
  qn_info?: string;
  correct_ans?: string;
  wrong_ans?: string;
  blank_ans?: string;
  opt_order?: string;
  EnteredCorrectAnswer?: string;
  EnteredWrongAnswer?: string;
}

interface PageState {
  loading: boolean;
  questions: QnTableRow[];
  currentIndex: number;
  headerInfo: HeaderInfo | null;
  warningMsg: string;
  questionDetails: QuestionData | null;
  iframeReady: boolean;
  qnHtmlOverride: string;
}

type Action =
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_DATA"; questions: QnTableRow[]; headerInfo: HeaderInfo }
  | { type: "GO_TO"; index: number }
  | { type: "SET_WARNING"; payload: string }
  | { type: "SET_QUESTION_DETAILS"; payload: QuestionData }
  | { type: "IFRAME_READY" }
  | { type: "SET_QN_HTML_OVERRIDE"; payload: string };

function reducer(state: PageState, action: Action): PageState {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, loading: action.payload };
    case "SET_DATA":
      return { ...state, questions: action.questions, headerInfo: action.headerInfo, currentIndex: 0, loading: false };
    case "GO_TO":
      return { ...state, currentIndex: action.index, iframeReady: false, qnHtmlOverride: "", questionDetails: null };
    case "SET_WARNING":
      return { ...state, warningMsg: action.payload };
    case "SET_QUESTION_DETAILS":
      return { ...state, questionDetails: action.payload, loading: false };
    case "IFRAME_READY":
      return { ...state, iframeReady: true, loading: false };
    case "SET_QN_HTML_OVERRIDE":
      return { ...state, qnHtmlOverride: action.payload };
    default:
      return state;
  }
}

const initialState: PageState = {
  loading: true,
  questions: [],
  currentIndex: 0,
  headerInfo: null,
  warningMsg: "",
  questionDetails: null,
  iframeReady: false,
  qnHtmlOverride: "",
};

// ─── Option-image helpers (identical to MD_ViewQuestion) ──────────────────────

const getOptionImageType = (html: string): number => {
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const mwInfo = (doc.getElementById("KSD_MW_Info") as HTMLInputElement | null)?.value ?? "";
    return parseInt(mwInfo.split("|")[8] ?? "0", 10) || 0;
  } catch { return 0; }
};

const getOptionImages = (type: number): { normal: string; selected: string } | null => {
  switch (type) {
    case 2: return { normal: large_objective_n,            selected: large_objective_d            };
    case 3: return { normal: medium_objective_n,           selected: medium_objective_d           };
    case 4: return { normal: small_objective_n,            selected: small_objective_d            };
    case 5: return { normal: large_objective_shortened_n,  selected: large_objective_shortened_d  };
    case 6: return { normal: medium_objective_shortened_n, selected: medium_objective_shortened_d };
    case 7: return { normal: small_objective_shortened_n,  selected: small_objective_shortened_d  };
    default: return null;
  }
};

// ─── Base CSS injector ────────────────────────────────────────────────────────

const injectBaseCSS = (html: string): string => {
  const doc        = new DOMParser().parseFromString(html, "text/html");
  const optionType = getOptionImageType(html);
  const imgConfig  = getOptionImages(optionType);
  const style      = doc.createElement("style");
  style.textContent = `
    [id^="KSD_TR_"] [id^="KSD_TD_"] {
      ${imgConfig
        ? `background-image: url(${imgConfig.normal}); background-repeat: no-repeat; background-color: transparent;`
        : `background-color: #F8F8F8;`}
      cursor: pointer;
      padding-top: 5px !important;
      padding-bottom: 5px !important;
    }
    [id^="KSD_TD_"] tbody > tr > td { font-size: 0px !important; }
  `;
  doc.head.appendChild(style);
  return doc.documentElement.outerHTML;
};

const hideElementsByIdInHtml = (html: string, ids: string[]): string => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  ids.forEach(id => { const el = doc.getElementById(id); if (el) el.style.display = "none"; });
  return doc.documentElement.outerHTML;
};

// ─── Type O ───────────────────────────────────────────────────────────────────

const injectTypeO = (
  html: string,
  enteredCorrectAnswer?: string,
  enteredWrongAnswer?: string,
  correctAnswerIndex?: string,
): string => {
  const doc        = new DOMParser().parseFromString(html, "text/html");
  const optionType = getOptionImageType(html);
  const imgConfig  = getOptionImages(optionType);

  const style = doc.createElement("style");
  style.textContent = `
    [id^="KSD_TR_"] [id^="KSD_TD_"] {
      ${imgConfig
        ? `background-image: url(${imgConfig.normal}); background-repeat: no-repeat; background-color: transparent;`
        : `background-color: #F8F8F8;`}
      cursor: pointer;
      padding-top: 5px !important;
      padding-bottom: 5px !important;
    }
  `;
  doc.head.appendChild(style);

  doc.querySelectorAll('[id^="KSD_TDANS_"]').forEach(td => {
    const m = (td.getAttribute("id") ?? "").match(/KSD_TDANS_(\d+)/);
    if (!m) return;
    const ansIdx = m[1];
    td.querySelectorAll("img").forEach(img => img.remove());

    const mkImg = (src: string, alt: string) => {
      const img = doc.createElement("img");
      img.src = src; img.alt = alt; img.style.verticalAlign = "middle";
      return img;
    };

    if (enteredCorrectAnswer !== undefined && ansIdx === enteredCorrectAnswer && enteredWrongAnswer === "") {
      td.appendChild(mkImg(AnsCorrect, "Correct"));
    } else if (enteredCorrectAnswer !== undefined && enteredWrongAnswer !== undefined && enteredWrongAnswer !== "") {
      if (ansIdx === enteredCorrectAnswer)  td.appendChild(mkImg(AnsShouldbe, "Should Be"));
      else if (ansIdx === enteredWrongAnswer) td.appendChild(mkImg(AnsWrong, "Wrong"));
    } else if (correctAnswerIndex !== undefined && ansIdx === correctAnswerIndex) {
      td.appendChild(mkImg(AnsShouldbe, "Should Be"));
    }
  });

  return doc.documentElement.outerHTML;
};

// ─── Type C ───────────────────────────────────────────────────────────────────

const parseTypeCAnswerIndices = (
  enteredCorrectAnswer: string,
  enteredWrongAnswer: string,
): { correctOriginalIndices: Set<number>; selectedCorrectIndices: Set<number>; selectedWrongIndices: Set<number> } => {
  const toNumSet = (raw: string): Set<number> => {
    const s = new Set<number>();
    if (!raw?.trim()) return s;
    raw.split("~").map(t => t.trim()).filter(Boolean).forEach(t => { const n = parseInt(t, 10); if (!isNaN(n)) s.add(n); });
    return s;
  };
  const hashParts = (enteredCorrectAnswer ?? "").split("#");
  return {
    correctOriginalIndices: toNumSet(hashParts[0] ?? ""),
    selectedCorrectIndices: toNumSet(hashParts[1] ?? ""),
    selectedWrongIndices:   toNumSet(enteredWrongAnswer ?? ""),
  };
};

const injectTypeC = (
  html: string,
  optOrder?: string,
  enteredCorrectAnswer?: string,
  enteredWrongAnswer?: string,
): string => {
  const doc      = new DOMParser().parseFromString(html, "text/html");
  const mwInfo   = (doc.getElementById("KSD_MW_Info") as HTMLInputElement | null)?.value ?? "";
  const mwParts  = mwInfo.split("|");
  const bgOdd    = mwParts[1] ?? "#F8F8F8";
  const bgEven   = mwParts[2] ?? "#FFFFFF";
  const optType  = parseInt(mwParts[8] ?? "0", 10) || 0;
  const styleStr = (mwParts[9] ?? "").toString().padStart(2, "0");
  const bgTransp = parseInt(styleStr[0] ?? "0", 10) === 1;
  const imgCfg   = getOptionImages(optType);
  const SEL_BG   = "#7a96df";

  // Re-order rows by opt_order
  if (optOrder?.trim()) {
    const order = optOrder.split("~").map(t => parseInt(t.trim(), 10)).filter(n => !isNaN(n));
    if (order.length > 0) {
      const tbody = doc.querySelector("#KSD_TABLE > tbody");
      if (tbody) {
        const rowMap = new Map<number, Element>();
        tbody.querySelectorAll("tr[id^='KSD_TR_']").forEach(tr => {
          const idx = parseInt((tr.getAttribute("id") ?? "").slice(7), 10);
          if (!isNaN(idx)) rowMap.set(idx, tr);
        });
        rowMap.forEach(tr => tr.parentNode?.removeChild(tr));
        order.forEach(origIdx => { const tr = rowMap.get(origIdx); if (tr) tbody.appendChild(tr); });
        tbody.querySelectorAll("tr[id^='KSD_TR_']").forEach((tr, vi) => {
          const label = tr.querySelector("font[id^='Ind_']");
          if (label) label.textContent = String.fromCharCode(65 + vi);
        });
      }
    }
  }

  doc.querySelectorAll("td[id='KSD_TD1']").forEach(td => { (td as HTMLElement).style.display = "none"; });
  doc.querySelectorAll("input[id^='Chk_']").forEach(input => { (input as HTMLInputElement).disabled = true; });

  const styleEl = doc.createElement("style");
  if (optType >= 2 && imgCfg) {
    styleEl.textContent = `
      #KSD_TABLE > tbody > tr[id^="KSD_TR_"] > td[id^="KSD_TD_"] {
        background-image: url(${imgCfg.normal}); background-repeat: no-repeat;
        background-color: transparent; cursor: pointer;
        padding-top: 5px !important; padding-bottom: 5px !important;
      }
      #KSD_TABLE > tbody > tr[id^="KSD_TR_"] { cursor: pointer; }
      [id^="KSD_TD_"] tbody > tr > td { font-size: 0px !important; }
    `;
  } else {
    const oddBg  = bgTransp ? "transparent" : bgOdd;
    const evenBg = bgTransp ? "transparent" : bgEven;
    styleEl.textContent = `
      #KSD_TABLE > tbody > tr[id^="KSD_TR_"]:nth-child(odd)  { background-color: ${oddBg};  cursor: pointer; }
      #KSD_TABLE > tbody > tr[id^="KSD_TR_"]:nth-child(even) { background-color: ${evenBg}; cursor: pointer; }
      #KSD_TABLE > tbody > tr[id^="KSD_TR_"] { padding-top: 5px !important; padding-bottom: 5px !important; }
      [id^="KSD_TD_"] tbody > tr > td { font-size: 0px !important; }
    `;
  }
  doc.head.appendChild(styleEl);

  const hasAnswerData =
    (enteredCorrectAnswer !== undefined && enteredCorrectAnswer.trim() !== "") ||
    (enteredWrongAnswer   !== undefined && enteredWrongAnswer.trim()   !== "");

  if (hasAnswerData) {
    const { correctOriginalIndices, selectedCorrectIndices, selectedWrongIndices } =
      parseTypeCAnswerIndices(enteredCorrectAnswer ?? "", enteredWrongAnswer ?? "");

    const ansSel = optType >= 2 ? "td[id^='KSD_TDANS_']" : "td[id^='TDANS_']";
    doc.querySelectorAll(ansSel).forEach(td => { (td as HTMLElement).style.display = ""; });

    doc.querySelectorAll("tr[id^='KSD_TR_']").forEach(tr => {
      const origIdx = parseInt((tr.getAttribute("id") ?? "").slice(7), 10);
      if (isNaN(origIdx)) return;
      const ansTd = tr.querySelector(ansSel) as HTMLElement | null;
      if (!ansTd) return;
      ansTd.querySelectorAll("img").forEach(img => img.remove());

      const isSelCorrect = selectedCorrectIndices.has(origIdx);
      const isSelWrong   = selectedWrongIndices.has(origIdx);
      const isCorrectOpt = correctOriginalIndices.has(origIdx);

      let imgSrc = "", imgAlt = "";
      if (isSelCorrect)    { imgSrc = AnsCorrect;  imgAlt = "Correct Answer"; }
      else if (isSelWrong) { imgSrc = AnsWrong;    imgAlt = "Incorrect Answer"; }
      else if (isCorrectOpt){ imgSrc = AnsShouldbe; imgAlt = "Correct Answer"; }

      if (imgSrc) {
        const img = doc.createElement("img");
        img.setAttribute("id", "imgR"); img.src = imgSrc; img.alt = imgAlt;
        img.style.verticalAlign = "middle"; ansTd.appendChild(img);
      }
      if (optType < 2 && (isSelCorrect || isSelWrong)) {
        (tr as HTMLElement).style.backgroundColor = SEL_BG;
      }
    });
  }

  return doc.documentElement.outerHTML;
};

// ─── Type F ───────────────────────────────────────────────────────────────────

const applyTypeFReviewMarkup = (
  doc: Document,
  enteredCorrect: string,
  enteredWrong: string,
  enteredBlank: string,
  correctAnswers: string[],
): void => {
  const makeSpan = (text: string, color: string, extra?: Partial<CSSStyleDeclaration>): HTMLSpanElement => {
    const span = doc.createElement("span");
    span.style.backgroundColor = "transparent";
    span.style.fontFamily      = "Verdana";
    span.style.color           = color;
    if (extra) Object.assign(span.style, extra);
    span.textContent = text;
    return span;
  };

  const makeTickImg = (): HTMLImageElement => {
    const img = doc.createElement("img");
    img.setAttribute("id", "imgR"); img.src = AnsCorrect;
    img.alt = "Correct Answer"; img.style.verticalAlign = "middle";
    return img;
  };

  const hasPosPrefix = (raw: string): boolean => {
    if (!raw?.trim()) return false;
    const tokens = raw.split("~").map(t => t.trim()).filter(Boolean);
    return tokens.length > 0 && tokens.every(t => t.length >= 2 && /^\d{2}/.test(t)) && tokens.some(t => t.length > 2);
  };

  const parseTokenMap = (raw: string): Map<number, string> => {
    const map = new Map<number, string>();
    if (!raw?.trim()) return map;
    if (hasPosPrefix(raw)) {
      for (const token of raw.split("~").map(t => t.trim()).filter(Boolean)) {
        if (token.length < 2) continue;
        const idx = parseInt(token.substring(0, 2), 10);
        if (!isNaN(idx)) map.set(idx, token.substring(2));
      }
    } else {
      raw.replace(/\|/g, "~").split("~").map(t => t.trim()).filter(Boolean)
        .forEach((val, i) => map.set(i, val));
    }
    return map;
  };

  const parseBlankSet = (raw: string): Set<number> => {
    const s = new Set<number>();
    if (!raw?.trim()) return s;
    raw.split("~").map(t => t.trim()).filter(Boolean).forEach(token => {
      const idx = parseInt(token, 10); if (!isNaN(idx)) s.add(idx);
    });
    return s;
  };

  const correctMap = parseTokenMap(enteredCorrect);
  const wrongMap   = parseTokenMap(enteredWrong);
  const blankSet   = parseBlankSet(enteredBlank);

  const isAllCorrect = enteredCorrect !== "" && enteredWrong === "" && enteredBlank === "";
  const isSkipped    = enteredCorrect === "" && enteredWrong === "" && enteredBlank === "";

  const getSpanAndInput = (idx: number) => {
    const spanHost = doc.getElementById(`span_${idx}`);
    const input    = (spanHost?.querySelector(`input[id='KSD_BLANK_${idx}']`)
                   ?? doc.getElementById(`KSD_BLANK_${idx}`)) as HTMLInputElement | null;
    return { spanHost, input };
  };

  if (isAllCorrect) {
    correctMap.forEach((val, idx) => {
      const { spanHost, input } = getSpanAndInput(idx);
      if (!spanHost) return;
      if (input) input.style.display = "none";
      spanHost.style.border = "none"; spanHost.style.borderBottom = "solid 2px red";
      const gs = makeSpan(val, "green"); gs.appendChild(makeTickImg()); spanHost.appendChild(gs);
    });
  } else if (enteredWrong !== "" || enteredBlank !== "") {
    correctMap.forEach((val, idx) => {
      const { spanHost, input } = getSpanAndInput(idx);
      if (!spanHost) return;
      if (input) input.style.display = "none";
      spanHost.style.border = "none"; spanHost.style.borderBottom = "solid 2px red";
      const gs = makeSpan(val, "green"); gs.appendChild(makeTickImg()); spanHost.appendChild(gs);
    });
    wrongMap.forEach((wrongVal, idx) => {
      const { spanHost, input } = getSpanAndInput(idx);
      if (!spanHost) return;
      if (input) input.style.display = "none";
      spanHost.style.border = "none"; spanHost.style.borderBottom = "solid 2px red";
      spanHost.appendChild(makeSpan(wrongVal, "red", { textDecoration: "line-through" }));
      spanHost.appendChild(doc.createTextNode("\u00a0"));
      spanHost.appendChild(makeSpan((correctAnswers[idx] ?? "").trim(), "green"));
    });
    blankSet.forEach(idx => {
      const { spanHost, input } = getSpanAndInput(idx);
      if (!spanHost) return;
      if (input) input.style.border = "solid 2px red";
      spanHost.style.border = "none"; spanHost.style.borderBottom = "solid 2px red";
      spanHost.appendChild(doc.createTextNode("\u00a0"));
      spanHost.appendChild(makeSpan((correctAnswers[idx] ?? "").trim(), "green"));
    });
  } else if (isSkipped) {
    doc.querySelectorAll("span[id^='span_']").forEach((spanHost, index) => {
      const input = spanHost.querySelector("input[id^='KSD_BLANK_']") as HTMLInputElement | null;
      if (input) { input.style.borderStyle = "none"; input.style.padding = "0px"; input.style.borderBottom = "solid 2px black"; }
      spanHost.appendChild(doc.createTextNode("\u00a0"));
      spanHost.appendChild(makeSpan((correctAnswers[index] ?? "").trim(), "red", { fontWeight: "bold" }));
    });
  }
};

const injectTypeF = (html: string, enteredCorrect: string, enteredWrong: string, enteredBlank: string): string => {
  const doc      = new DOMParser().parseFromString(html, "text/html");
  const mwParts  = ((doc.getElementById("KSD_MW_Info") as HTMLInputElement | null)?.value ?? "").split("|");
  const hasAnsLst    = (mwParts[3] ?? "0") === "1";
  const fOptImgType  = mwParts[11] ?? "0";
  const correctAnswers = ((doc.getElementById("KSD_BW") as HTMLInputElement | null)?.value ?? "").split("|").map(v => v.trim());

  const dividerBar = doc.getElementById("KSD_DIVIDERBAR"); if (dividerBar) dividerBar.parentNode?.removeChild(dividerBar);
  const numPad     = doc.getElementById("KSD_NUMPAD");     if (numPad)     numPad.parentNode?.removeChild(numPad);
  if (!hasAnsLst) { const w2fu = doc.getElementById("KSD_WORDS2FILLUP"); if (w2fu) w2fu.parentNode?.removeChild(w2fu); }

  applyTypeFReviewMarkup(doc, enteredCorrect, enteredWrong, enteredBlank, correctAnswers);
  doc.querySelectorAll("input[id^='KSD_BLANK_']").forEach(input => { (input as HTMLInputElement).readOnly = true; });

  if (hasAnsLst) {
    const cellH = fOptImgType === "4" ? "40px" : "55px";
    const styleEl = doc.createElement("style");
    styleEl.textContent = `
      td[id^='KSD_FIGTD_'] {
        background-image: linear-gradient(to bottom, #f0f7ff 0%, #dceeff 50%, #c8e3ff 100%) !important;
        background-color: transparent !important;
        border: 2px solid #a8c4e0 !important;
        border-radius: 4px !important;
        height: ${cellH} !important;
        min-width: 120px !important;
        text-align: center !important;
        cursor: default !important;
        pointer-events: none !important;
        font-family: Arial, sans-serif !important;
        color: #374d64 !important;
        padding: 4px 8px !important;
        box-sizing: border-box !important;
      }
      td[id^='KSD_FIGTD_'] span { color: #374d64 !important; font-size: 14pt !important; }
    `;
    doc.head.appendChild(styleEl);
    doc.querySelectorAll("td[id^='KSD_FIGTD_']").forEach(td => {
      ["onmousedown","onmousemove","onmouseout","onmouseup","onclick"].forEach(ev => td.removeAttribute(ev));
    });
  }

  return doc.documentElement.outerHTML;
};

// ─── Type N ───────────────────────────────────────────────────────────────────

const injectTypeN = (html: string, enteredCorrect: string, enteredWrong: string, enteredBlank: string): string => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.querySelectorAll("input[id^='KSD_BLANK_'], input[id^='TXT_CARRY_']").forEach(input => {
    (input as HTMLInputElement).readOnly = true;
  });

  const correctAnswers = ((doc.getElementById("KSD_BW") as HTMLInputElement | null)?.value ?? "").split("|").map(v => v.trim());

  const hasPosPrefix = (raw: string): boolean => {
    if (!raw?.trim()) return false;
    const tokens = raw.split("~").map(t => t.trim()).filter(Boolean);
    return tokens.length > 0 && tokens.every(t => t.length >= 2 && /^\d{2}/.test(t)) && tokens.some(t => t.length > 2);
  };

  const parseTokens = (raw: string): Map<number, string> => {
    const map = new Map<number, string>();
    if (!raw?.trim()) return map;
    if (hasPosPrefix(raw)) {
      for (const token of raw.split("~").map(t => t.trim()).filter(Boolean)) {
        if (token.length < 2) continue;
        const idx = parseInt(token.substring(0, 2), 10); if (!isNaN(idx)) map.set(idx, token.substring(2));
      }
    } else {
      raw.replace(/\|/g, "~").split("~").map(t => t.trim()).filter(Boolean).forEach((val, i) => map.set(i, val));
    }
    return map;
  };

  const parseIdxSet = (raw: string): Set<number> => {
    const s = new Set<number>();
    if (!raw?.trim()) return s;
    raw.split("~").map(x => x.trim()).filter(Boolean).forEach(t => { const n = parseInt(t, 10); if (!isNaN(n)) s.add(n); });
    return s;
  };

  const splitParts = (raw: string): [string, string] => {
    const ci = raw.indexOf(","); return ci === -1 ? [raw, ""] : [raw.substring(0, ci), raw.substring(ci + 1)];
  };

  const [blankCorrectRaw, carryCorrectRaw] = splitParts(enteredCorrect);
  const [blankWrongRaw,   carryWrongRaw  ] = splitParts(enteredWrong);
  const [blankBlankRaw,   carryBlankRaw  ] = splitParts(enteredBlank);

  const blankCorrectMap = parseTokens(blankCorrectRaw);
  const carryCorrectMap = parseTokens(carryCorrectRaw);
  const blankWrongMap   = parseTokens(blankWrongRaw);
  const carryWrongMap   = parseTokens(carryWrongRaw);
  const blankBlankSet   = parseIdxSet(blankBlankRaw);
  const carryBlankSet   = parseIdxSet(carryBlankRaw);

  const makeSpan = (text: string, css: Partial<CSSStyleDeclaration>): HTMLSpanElement => {
    const span = doc.createElement("span");
    Object.assign(span.style, { backgroundColor: "transparent", border: "none", ...css });
    span.textContent = text; return span;
  };

  const insertAfter = (newEl: Element, refEl: Element) => refEl.parentNode?.insertBefore(newEl, refEl.nextSibling);

  const isAllBlankCorrect  = blankCorrectRaw.trim() !== "" && blankWrongRaw.trim() === "";
  const isSomeWrongOrBlank = blankWrongRaw.trim() !== "" || enteredBlank.trim() !== "";
  const isSkipped          = enteredCorrect.trim() === "" && enteredWrong.trim() === "" && enteredBlank.trim() === "";

  if (isAllBlankCorrect) {
    blankCorrectMap.forEach((val, idx) => {
      const input = (doc.querySelector(`input[id='KSD_BLANK_${idx}']`) ?? doc.querySelectorAll("input[id^='KSD_BLANK_']")[idx]) as HTMLElement | null;
      if (!input) return;
      insertAfter(makeSpan(val, { color: "green", borderBottom: "2px solid red" }), input);
      (input as HTMLElement).style.display = "none";
    });
    carryCorrectMap.forEach((val, idx) => {
      const input = doc.querySelector(`input[id='TXT_CARRY_${idx}']`) as HTMLElement | null;
      if (!input) return;
      insertAfter(makeSpan(val, { fontSize: "15pt", marginLeft: "20px" }), input);
      (input as HTMLElement).style.display = "none";
    });
    carryWrongMap.forEach((val, idx) => {
      const input = doc.querySelector(`input[id='TXT_CARRY_${idx}']`) as HTMLElement | null;
      if (!input) return;
      const ps = makeSpan("", { fontSize: "15pt", marginLeft: "20px" }); ps.appendChild(makeSpan(val, {}));
      insertAfter(ps, input); (input as HTMLElement).style.display = "none";
    });
  } else if (isSomeWrongOrBlank) {
    blankCorrectMap.forEach((val, idx) => {
      const input = doc.querySelector(`input[id='KSD_BLANK_${idx}']`) as HTMLElement | null;
      if (!input) return;
      insertAfter(makeSpan(val, { color: "green", borderBottom: "2px solid red" }), input);
      (input as HTMLElement).style.display = "none";
    });
    carryCorrectMap.forEach((val, idx) => {
      const input = doc.querySelector(`input[id='TXT_CARRY_${idx}']`) as HTMLElement | null;
      if (!input) return;
      insertAfter(makeSpan(val, { fontSize: "15pt", marginLeft: "20px" }), input);
      (input as HTMLElement).style.display = "none";
    });
    blankWrongMap.forEach((wrongVal, idx) => {
      const input = doc.querySelector(`input[id='KSD_BLANK_${idx}']`) as HTMLElement | null;
      if (!input) return;
      const correctVal = (correctAnswers[idx] ?? "").trim();
      const ps = makeSpan("", { borderBottom: "2px solid red" });
      ps.appendChild(makeSpan(wrongVal, { color: "red", backgroundColor: "yellow", textDecoration: "line-through" }));
      ps.appendChild(doc.createTextNode("\u00a0"));
      ps.appendChild(makeSpan(correctVal, { color: "green" }));
      insertAfter(ps, input); (input as HTMLElement).style.display = "none";
    });
    carryWrongMap.forEach((wrongVal, idx) => {
      const input = doc.querySelector(`input[id='TXT_CARRY_${idx}']`) as HTMLElement | null;
      if (!input) return;
      const ps = makeSpan("", { fontSize: "15pt", marginLeft: "20px" }); ps.appendChild(makeSpan(wrongVal, {}));
      insertAfter(ps, input); (input as HTMLElement).style.display = "none";
    });
    blankBlankSet.forEach(idx => {
      const input = doc.querySelector(`input[id='KSD_BLANK_${idx}']`) as HTMLInputElement | null;
      if (!input) return;
      input.style.backgroundColor = "yellow"; input.style.border = "1px dashed black";
      insertAfter(makeSpan((correctAnswers[idx] ?? "").trim(), { color: "green" }), input);
    });
    carryBlankSet.forEach(idx => {
      const input = doc.querySelector(`input[id='TXT_CARRY_${idx}']`) as HTMLInputElement | null;
      if (!input) return;
      input.style.border = "1px dashed black";
      insertAfter(makeSpan("", { color: "green", fontSize: "15pt", marginLeft: "20px" }), input);
    });
  } else if (isSkipped) {
    doc.querySelectorAll("input[id^='KSD_BLANK_'], input[id^='TXT_CARRY_']").forEach(input => {
      (input as HTMLElement).style.border = "1px dashed black";
    });
    doc.querySelectorAll("input[id^='KSD_BLANK_']").forEach((input, index) => {
      insertAfter(makeSpan((correctAnswers[index] ?? "").trim(), { color: "green" }), input);
    });
    doc.querySelectorAll("input[id^='TXT_CARRY_']").forEach(input => {
      const carryName = (input as HTMLInputElement).name ?? "";
      if (carryName === "-" || carryName === "") return;
      insertAfter(makeSpan(carryName, { fontSize: "15pt", marginLeft: "20px" }), input);
    });
  }

  return doc.documentElement.outerHTML;
};

// ─── Type W ───────────────────────────────────────────────────────────────────

const injectTypeW = (html: string, enteredCorrect: string, enteredWrong: string): string => {
  const doc = new DOMParser().parseFromString(html, "text/html");

  const ksdW = doc.getElementById("KSD_W") as HTMLTextAreaElement | HTMLInputElement | null;
  if (ksdW) {
    (ksdW as HTMLInputElement).disabled = true;
    ksdW.style.border = "solid 2px skyblue";
    ksdW.style.backgroundColor = "white";
    ksdW.style.opacity = "1";
  }

  const wPad       = doc.getElementById("KSD_W_PAD");     if (wPad)       wPad.parentNode?.removeChild(wPad);
  const dividerBar = doc.getElementById("KSD_DIVIDERBAR"); if (dividerBar) dividerBar.parentNode?.removeChild(dividerBar);
  const numpad     = doc.getElementById("KSD_NUMPAD");     if (numpad)     numpad.parentNode?.removeChild(numpad);

  const mwCaVal = ((doc.getElementById("KSD_MW_CA") as HTMLInputElement | null)?.value ?? "").split("^^")[0];

  const injectImg = (tdId: string, src: string, alt: string) => {
    const td = doc.getElementById(tdId); if (!td) return;
    td.innerHTML = "";
    const img = doc.createElement("img");
    img.setAttribute("id", "imgR"); img.src = src; img.alt = alt; img.style.verticalAlign = "middle";
    td.appendChild(img); (td as HTMLElement).style.display = "";
  };

  if (enteredCorrect !== "" && enteredWrong === "") {
    injectImg("KSD_TDANS_0", AnsCorrect, "Correct Answer");
    if (ksdW) (ksdW as HTMLInputElement).value = enteredCorrect;
  } else if (enteredCorrect === "" && enteredWrong !== "") {
    injectImg("KSD_TDANS_0", AnsWrong,    "Incorrect Answer");
    if (ksdW) (ksdW as HTMLInputElement).value = enteredWrong;
    injectImg("KSD_TDANS_1", AnsShouldbe, "Correct Answer");
    const tdansOk = doc.getElementById("KSD_TDANSOK");
    if (tdansOk) {
      tdansOk.textContent = mwCaVal; tdansOk.style.display = "";
      tdansOk.style.fontFamily = "Verdana"; tdansOk.style.color = "green";
      tdansOk.style.fontWeight = "normal"; tdansOk.style.fontSize = "15pt";
    }
  } else {
    injectImg("KSD_TDANS_0", AnsWrong,    "Incorrect Answer");
    injectImg("KSD_TDANS_1", AnsShouldbe, "Correct Answer");
    const tdansOk = doc.getElementById("KSD_TDANSOK");
    if (tdansOk) {
      tdansOk.textContent = mwCaVal; tdansOk.style.display = "";
      tdansOk.style.fontFamily = "Verdana"; tdansOk.style.color = "green";
      tdansOk.style.fontWeight = "normal"; tdansOk.style.fontSize = "15pt";
    }
  }

  return doc.documentElement.outerHTML;
};

// ─── API helper ───────────────────────────────────────────────────────────────

async function apiFetch<T>(url: string): Promise<T> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status} – ${url}`);
  return res.json() as Promise<T>;
}

// ─── Component ────────────────────────────────────────────────────────────────

const MD_ViewQuestionAnswer: React.FC = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [searchParams]    = useSearchParams();
  const iframeRef         = useRef<HTMLIFrameElement>(null);
  const headerRef         = useRef<HTMLDivElement>(null);

  const schoolCode = searchParams.get("StuSch")    || "";
  const familyCode = searchParams.get("StuFam")    || "";
  const loginId    = searchParams.get("StudentId") || "";
  const reportNo   = searchParams.get("ReportNo")  || "";

  document.body.style.backgroundColor = "#fff";

  // ── Fetch initial report data ─────────────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      if (!schoolCode || !familyCode || !loginId || !reportNo) {
        dispatch({ type: "SET_WARNING", payload: "Missing required parameters: StuSch, StuFam, StudentId, or ReportNo." });
        dispatch({ type: "SET_LOADING", payload: false });
        return;
      }

      try {
        const query = new URLSearchParams({ SchoolCode: schoolCode, FamilyCode: familyCode, Login: loginId, ReportNo: reportNo });
        const res   = await fetch(`${API_BASE_URL}api/get_navigation_report?${query}`);

        if (!res.ok) {
          dispatch({ type: "SET_WARNING", payload: `Failed to load report: HTTP ${res.status}` });
          dispatch({ type: "SET_LOADING", payload: false });
          return;
        }

        const result = await res.json();

        if (!result || typeof result !== "object") {
          dispatch({ type: "SET_WARNING", payload: "Invalid response format from server." });
          dispatch({ type: "SET_LOADING", payload: false });
          return;
        }
        if (result.status !== "success") {
          dispatch({ type: "SET_WARNING", payload: result.message || "Failed to load questions." });
          dispatch({ type: "SET_LOADING", payload: false });
          return;
        }
        if (!result.data) {
          dispatch({ type: "SET_WARNING", payload: "No data returned from server." });
          dispatch({ type: "SET_LOADING", payload: false });
          return;
        }
        if (!result.data.qn_table || !Array.isArray(result.data.qn_table)) {
          dispatch({ type: "SET_WARNING", payload: "Invalid question table structure." });
          dispatch({ type: "SET_LOADING", payload: false });
          return;
        }
        if (result.data.qn_table.length === 0) {
          dispatch({ type: "SET_WARNING", payload: "No questions found for this report." });
          dispatch({ type: "SET_LOADING", payload: false });
          return;
        }
        if (!result.data.header_info) {
          dispatch({ type: "SET_WARNING", payload: "Missing header information." });
          dispatch({ type: "SET_LOADING", payload: false });
          return;
        }

        dispatch({ type: "SET_DATA", questions: result.data.qn_table, headerInfo: result.data.header_info });
      } catch (error) {
        dispatch({ type: "SET_WARNING", payload: `Network error: ${error instanceof Error ? error.message : String(error)}` });
        dispatch({ type: "SET_LOADING", payload: false });
      }
    };

    fetchData();
  }, [schoolCode, familyCode, loginId, reportNo]);

  // ── Fetch question details whenever currentIndex changes ──────────────────
  useEffect(() => {
    if (state.questions.length === 0 || !state.headerInfo) return;
    const currentQ = state.questions[state.currentIndex];
    if (!currentQ) return;

    const fetchQnDetails = async () => {
      dispatch({ type: "SET_LOADING", payload: true });
      try {
        const hi         = state.headerInfo!;
        const country    = hi.Country || hi.MDCurriculum || "";
        const curriculum = hi.MDCurriculum || country;

        const params = new URLSearchParams({
          source:      "navreport",
          strand:      String(currentQ.Strand),
          substrand:   String(currentQ.SubStrand),
          yr:          String(currentQ.Yr),
          topic:       String(currentQ.Topic),
          qn_no:       String(currentQ.QnNo),
          country,
          curriculum,
          correct_ans: currentQ.CorrectAns  ?? "",
          wrong_ans:   currentQ.WrongAns    ?? "",
          opt_order:   currentQ.OptOrder    ?? "",
          blank_ans:   currentQ.BlankAns    ?? "",
          StuSch:      schoolCode,
          StuFam:      familyCode,
          StudentId:   loginId,
          ReportNo:    reportNo,
        });

        const data = await apiFetch<QuestionData>(`${API_BASE_URL}api/by-report-params/?${params}`);
        dispatch({ type: "SET_QUESTION_DETAILS", payload: data });
      } catch (e) {
        dispatch({ type: "SET_WARNING", payload: String(e) });
        dispatch({ type: "SET_LOADING", payload: false });
      }
    };

    fetchQnDetails();
  }, [state.currentIndex, state.questions, state.headerInfo, schoolCode, familyCode, loginId, reportNo]);

  // ── iframe resize ─────────────────────────────────────────────────────────
  const resizeIframe = useCallback(() => {
    const iframe  = iframeRef.current;
    const header  = headerRef.current;
    if (!iframe) return;
    const h = window.innerHeight - (header?.offsetHeight ?? 100) - 4;
    iframe.style.height = `${Math.max(h, 300)}px`;
    iframe.style.width  = "100%";
  }, []);

  useEffect(() => {
    resizeIframe();
    window.addEventListener("resize", resizeIframe);
    return () => window.removeEventListener("resize", resizeIframe);
  }, [resizeIframe, state.questionDetails]);

  // ── iframe onLoad — mirrors MD_ViewQuestion answer injection logic ─────────
  const handleIframeLoad = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe || !state.questionDetails) return;
    dispatch({ type: "IFRAME_READY" });

    const qd = state.questionDetails;

    // Send legacy bridge init (same as MD_ViewQuestion, report/review mode)
    iframe.contentWindow?.postMessage({
      type: "MD_INIT", QnType: qd.qn_catg, KatMode: 1, TestMode: 0,
      TestRepMode: 1, showVirtualKeypad: "N", hidQnInfo: qd.qn_info ?? "", ShowWAns: "Y",
    }, "*");

    // Type O
    if (qd.qn_catg === "O" && qd.qn_html) {
      let resolvedCorrectIndex = qd.EnteredCorrectAnswer ?? "";
      if (!resolvedCorrectIndex) {
        try {
          const mwInfo = (new DOMParser().parseFromString(qd.qn_html, "text/html")
            .getElementById("KSD_MW_Info") as HTMLInputElement | null)?.value ?? "";
          const idx = parseInt(mwInfo.split("|")[3] ?? "", 10);
          if (!isNaN(idx)) resolvedCorrectIndex = String(idx);
        } catch { /* ignore */ }
      }
      dispatch({ type: "SET_QN_HTML_OVERRIDE", payload: injectTypeO(qd.qn_html, resolvedCorrectIndex, qd.EnteredWrongAnswer, qd.correct_ans) });
    }

    // Type C
    if (qd.qn_catg === "C" && qd.qn_html) {
      dispatch({ type: "SET_QN_HTML_OVERRIDE", payload: injectTypeC(qd.qn_html, qd.opt_order, qd.EnteredCorrectAnswer, qd.EnteredWrongAnswer) });
    }

    // Type F
    if (qd.qn_catg === "F" && qd.qn_html) {
      try { dispatch({ type: "SET_QN_HTML_OVERRIDE", payload: injectTypeF(qd.qn_html, qd.correct_ans ?? "", qd.wrong_ans ?? "", qd.blank_ans ?? "") }); }
      catch (err) { console.error("injectTypeF error:", err); }
    }

    // Type N
    if (qd.qn_catg === "N" && qd.qn_html) {
      try { dispatch({ type: "SET_QN_HTML_OVERRIDE", payload: injectTypeN(qd.qn_html, qd.correct_ans ?? "", qd.wrong_ans ?? "", qd.blank_ans ?? "") }); }
      catch (err) { console.error("injectTypeN error:", err); }
    }

    // Type W
    if (qd.qn_catg === "W" && qd.qn_html) {
      try { dispatch({ type: "SET_QN_HTML_OVERRIDE", payload: injectTypeW(qd.qn_html, qd.correct_ans ?? "", qd.wrong_ans ?? "") }); }
      catch (err) { console.error("injectTypeW error:", err); }
    }

    resizeIframe();
  }, [state.questionDetails, resizeIframe]);

  // ── srcDoc builder (identical logic to MD_ViewQuestion) ───────────────────
  const buildSrcDoc = (qd: QuestionData): string | undefined => {
    if (!qd.qn_html) return undefined;
    if (state.qnHtmlOverride) return state.qnHtmlOverride;
    // F, N, W handle sidebar removal internally — don't pre-hide
    if (qd.qn_catg === "F" || qd.qn_catg === "N" || qd.qn_catg === "W") {
      return injectBaseCSS(qd.qn_html);
    }
    return injectBaseCSS(hideElementsByIdInHtml(qd.qn_html, ["KSD_DIVIDERBAR", "KSD_NUMPAD", "KSD_WORDS2FILLUP"]));
  };

  const dismissWarning = () => dispatch({ type: "SET_WARNING", payload: "" });

  const totalQuestions = state.questions.length;
  const canPrev        = state.currentIndex > 0;
  const canNext        = state.currentIndex < totalQuestions - 1;
  const qd             = state.questionDetails;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      {/* ── Top popup bar ── */}
      <div style={{
        backgroundImage: `url(${popimg}), url(${popbg})`,
        backgroundRepeat: "no-repeat, no-repeat",
        backgroundPosition: "left center, right center",
        backgroundSize: "30% 100%, 70% 100%",
        height: "16px", color: "white", padding: "7px 0px",
        fontWeight: "bold", fontSize: "12pt", fontFamily: "arial",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <span style={{ marginLeft: "10px" }}>Answer Sheet</span>
        <img src={popclose} alt="close" height="30px" style={{ cursor: "pointer" }} onClick={() => window.close()} />
      </div>

      <div style={{ width: "100%", height: "100vh", overflow: "hidden", position: "relative", fontFamily: "Arial", fontSize: 11 }}>

        {/* ── Loading overlay ── */}
        {state.loading && (
          <div style={{ position: "absolute", inset: 0, backgroundColor: "rgba(255,255,255,0.7)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Loading />
          </div>
        )}

        {/* ── Warning modal ── */}
        {state.warningMsg && (
          <div style={css.modalOverlay}>
            <div style={css.modal}>
              <p style={{ marginBottom: 0 }}>{state.warningMsg}</p>
              <button style={css.modalBtn} onClick={dismissWarning}>OK</button>
            </div>
          </div>
        )}

        {/* ── Header bar with question info + PREV/NEXT ── */}
        <div
          ref={headerRef}
          style={{
            backgroundImage: `url(${bg})`,
            backgroundRepeat: "repeat",
            width: "100%",
            boxSizing: "border-box" as const,
            padding: "6px 10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap" as const,
            gap: "6px",
          }}
        >
          {/* Left: question metadata */}
          <div style={{ display: "flex", flexDirection: "column" as const, gap: "2px" }}>
            {qd && (
              <div style={{ color: "white", fontSize: 14, whiteSpace: "nowrap" as const }}>
                Yr:&nbsp;<CssLabel>{String(qd.year)}</CssLabel>&nbsp;&nbsp;
                Str:&nbsp;<CssLabel>{qd.strand_label}</CssLabel>&nbsp;&nbsp;
                SS:&nbsp;<CssLabel>{qd.substrand_label}</CssLabel>
              </div>
            )}
            {qd && (
              <div style={{ color: "white", fontSize: 14, whiteSpace: "nowrap" as const }}>
                Top:&nbsp;<CssLabel>{qd.topic_label}</CssLabel>&nbsp;&nbsp;
                Lsn:&nbsp;<CssLabel>{qd.lesson_code}</CssLabel>&nbsp;&nbsp;
                QnNo:&nbsp;<CssLabel>{String(qd.qn_no)}</CssLabel>
              </div>
            )}
          </div>

          {/* Right: position counter + PREV / NEXT */}
          <div style={{ display: "flex", alignItems: "center", gap: "10px", flexShrink: 0 }}>
            <span style={{ color: "white", fontWeight: "bold", fontSize: "13pt", whiteSpace: "nowrap" as const }}>
              {totalQuestions > 0 ? `${state.currentIndex + 1} / ${totalQuestions}` : ""}
            </span>
            <button
              style={{ ...css.navBtn, opacity: canPrev ? 1 : 0.45, cursor: canPrev ? "pointer" : "not-allowed" }}
              onClick={() => canPrev && dispatch({ type: "GO_TO", index: state.currentIndex - 1 })}
              disabled={!canPrev}
            >
              ◀ PREV
            </button>
            <button
              style={{ ...css.navBtn, opacity: canNext ? 1 : 0.45, cursor: canNext ? "pointer" : "not-allowed" }}
              onClick={() => canNext && dispatch({ type: "GO_TO", index: state.currentIndex + 1 })}
              disabled={!canNext}
            >
              NEXT ▶
            </button>
          </div>
        </div>

        {/* ── Question iframe ── */}
        {qd ? (
          <div style={{ position: "relative", width: "100%" }}>
            <iframe
              ref={iframeRef}
              key={`${qd.qn_file_url}|${state.qnHtmlOverride ? "override" : qd.qn_html ? "srcdoc" : "src"}`}
              src={state.qnHtmlOverride || qd.qn_html ? undefined : qd.qn_file_url}
              srcDoc={buildSrcDoc(qd)}
              onLoad={handleIframeLoad}
              frameBorder={0}
              style={{ display: "block", width: "100%", border: "none", overflow: "auto" }}
              title="Question"
            />
          </div>
        ) : (
          !state.loading && (
            <div style={{ padding: "20px", textAlign: "center" as const, color: "#666" }}>
              Loading question…
            </div>
          )
        )}
      </div>
    </>
  );
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const CssLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span style={{
    backgroundColor: "yellow", fontWeight: "bold", fontSize: "13pt",
    color: "blue", fontFamily: "Calibri", paddingLeft: 4, paddingRight: 4,
  }}>
    {children}
  </span>
);

// ─── Styles ───────────────────────────────────────────────────────────────────

const css: Record<string, React.CSSProperties> = {
  modalOverlay: {
    position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.45)",
    zIndex: 99999, display: "flex", alignItems: "center", justifyContent: "center",
  },
  modal: {
    backgroundColor: "white", padding: "24px 32px", borderRadius: 4,
    minWidth: 240, textAlign: "center", fontFamily: "Arial",
    fontSize: 14, boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
  },
  modalBtn: {
    marginTop: 16, padding: "4px 24px", cursor: "pointer", fontSize: 13,
    backgroundColor: "#2871D3", color: "white", border: "none", borderRadius: 3,
  },
  navBtn: {
    padding: "5px 14px", backgroundColor: "#2871D3", color: "white",
    border: "none", borderRadius: 3, fontSize: "10pt", fontWeight: "bold",
  },
};

export default MD_ViewQuestionAnswer;